/* eslint-disable jira/filename-case */
import type { JiraSpaRoute, Match, RouterContext } from '@atlassian/react-resource-router';
import type { RouteName } from '@jira/platform__routing-route-name-types/src/RouteName.tsx';
import { ENTITY_ID, MENU_ID_CUSTOMER_SERVICE } from '../../../constants.tsx';
import type { SelectedPath } from '../../../types.tsx';

const routes = new Set<RouteName>([
	'helpcenter-settings-get-started',
	'csm-experience-get-started',
	'helpcenter-settings-details',
	'csm-experience-details',
	'helpcenter-settings-forms',
	'helpcenter-settings-form-edit',
	'csm-experience-forms',
	'csm-experience-form-edit',
	'helpcenter-settings-articles',
	'csm-experience-articles',
	'helpcenter-settings-articles-view-article',
	'helpcenter-settings-create-article',
	'helpcenter-settings-branding',
	'csm-experience-branding',
	'helpcenter-settings-support-website-details-edit',
	'csm-experience-support-website-edit',
	'helpcenter-settings-support-website-details-overview',
	'csm-experience-support-website-overview',
	'helpcenter-settings-support-website-details-pages',
	'csm-experience-support-website-pages',
	'helpcenter-settings-email',
	'csm-experience-email',
	'helpcenter-settings-widget-configuration-customize',
	'csm-experience-widget-configuration-customize',
	'helpcenter-settings-widget-configuration-install',
	'csm-experience-widget-configuration-install',
	'helpcenter-settings-embedded-widgets',
	'csm-experience-embedded-widgets',
	'helpcenter-settings-phone-configuration',
	'csm-experience-phone-configuration',
	'helpcenter-settings-chat-configuration',
	'csm-experience-chat-configuration',
	'helpcenter-settings-customer-access',
	'csm-experience-customer-access',
	'helpcenter-settings-agent-studio',
	'csm-experience-agent-studio',
	'helpcenter-settings-customer-notifications',
	'csm-experience-customer-notifications',
]);

function convert(routeContext: RouterContext<JiraSpaRoute>): SelectedPath {
	const routeName: RouteName = routeContext.route.name;
	const path: string[] = [MENU_ID_CUSTOMER_SERVICE];
	return convertRouteToPath({ routeName, path, match: routeContext.match });
}

function convertNew(args: { routeName: RouteName; match: Match }): SelectedPath {
	const { routeName, match } = args;
	const path: string[] = [MENU_ID_CUSTOMER_SERVICE];
	return convertRouteToPath({ routeName, path, match });
}

function convertRouteToPath(args: {
	routeName: RouteName;
	match: Match;
	path: string[];
}): SelectedPath {
	const { routeName, match, path } = args;
	const { projectKey } = match.params;
	projectKey && path.push(ENTITY_ID.CUSTOMER_EXPERIENCE(projectKey));

	switch (routeName) {
		case 'helpcenter-settings-get-started':
		case 'csm-experience-get-started': {
			const { helpCenterId } = match.params;
			helpCenterId &&
				path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'get-started'));
			break;
		}

		case 'helpcenter-settings-details':
		case 'csm-experience-details': {
			const { helpCenterId } = match.params;
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'details'));
			break;
		}

		case 'helpcenter-settings-forms':
		case 'helpcenter-settings-form-edit':
		case 'csm-experience-forms':
		case 'csm-experience-form-edit': {
			const { helpCenterId } = match.params;
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'forms'));
			break;
		}

		case 'helpcenter-settings-articles':
		case 'csm-experience-articles':
		case 'helpcenter-settings-articles-view-article':
		case 'helpcenter-settings-create-article': {
			const { helpCenterId } = match.params;
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'articles'));
			break;
		}

		case 'helpcenter-settings-branding':
		case 'csm-experience-branding': {
			const { helpCenterId } = match.params;
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'branding'));
			break;
		}

		case 'helpcenter-settings-support-website-details-edit':
		case 'csm-experience-support-website-edit':
		case 'helpcenter-settings-support-website-details-overview':
		case 'csm-experience-support-website-overview':
		case 'helpcenter-settings-support-website-details-pages':
		case 'csm-experience-support-website-pages': {
			const { helpCenterId } = match.params;
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCES_CHANNELS(helpCenterId));
			helpCenterId &&
				path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'support-website'));
			break;
		}

		case 'helpcenter-settings-email':
		case 'csm-experience-email': {
			const { helpCenterId } = match.params;
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCES_CHANNELS(helpCenterId));
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'email'));
			break;
		}

		case 'helpcenter-settings-widget-configuration-customize':
		case 'csm-experience-widget-configuration-customize':
		case 'helpcenter-settings-widget-configuration-install':
		case 'csm-experience-widget-configuration-install':
		case 'helpcenter-settings-embedded-widgets':
		case 'csm-experience-embedded-widgets': {
			const { helpCenterId } = match.params;
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCES_CHANNELS(helpCenterId));
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'embed'));
			break;
		}

		case 'helpcenter-settings-phone-configuration':
		case 'csm-experience-phone-configuration': {
			const { helpCenterId } = match.params;
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCES_CHANNELS(helpCenterId));
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'phone'));
			break;
		}

		case 'helpcenter-settings-chat-configuration':
		case 'csm-experience-chat-configuration': {
			const { helpCenterId } = match.params;
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'chat'));
			break;
		}

		case 'helpcenter-settings-customer-access':
		case 'csm-experience-customer-access': {
			const { helpCenterId } = match.params;
			helpCenterId &&
				path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'customer-access'));
			break;
		}

		case 'helpcenter-settings-agent-studio':
		case 'csm-experience-agent-studio': {
			const { helpCenterId } = match.params;
			helpCenterId && path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'agent'));
			break;
		}

		case 'helpcenter-settings-customer-notifications':
		case 'csm-experience-customer-notifications': {
			const { helpCenterId } = match.params;
			helpCenterId &&
				path.push(ENTITY_ID.CUSTOMER_EXPERIENCE_MENU_ITEM(helpCenterId, 'customer-notifications'));
			break;
		}

		default:
	}

	return path;
}

export const customerExperiencesConverter: {
	routes: Set<RouteName>;
	convert: typeof convert;
	convertNew: typeof convertNew;
} = { routes, convert, convertNew };
